import { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";
import { HiX, HiShieldCheck } from "react-icons/hi";
import { IoCheckmarkSharp } from "react-icons/io5";
import { FiInfo } from "react-icons/fi";

import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { ClipQualityIcon } from "MVVM/Components/Icons";
import { useUser } from "MVVM/Hooks/useUser";
import { useClipQualitySettingsModal } from "MVVM/Hooks/useClipQualitySettingsModal";
import { useClipQualityConfigs } from "MVVM/Hooks/useClipQualityConfigs";
import { analytics } from "MVVM/Analytics";
import { useAutoSave } from "./useAutoSave";

const AUTO_SAVE_FILTERS = [
  {
    id: "all",
    label: "Just save everything",
    description: "Every clip you create will be saved automatically",
  },
  {
    id: "aces",
    label: "Aces only",
    description: "Only auto-save your ace rounds",
  },
  {
    id: "4k",
    label: "4Ks and above",
    description: "Auto-save your 4Ks and aces",
  },
  {
    id: "3k",
    label: "3Ks and above",
    description: "Auto-save your 3Ks, 4Ks, and aces",
  },
];

interface ClipSettingsModalProps {
  onClose: () => void;
}

export const ClipSettingsModal = ({ onClose }: ClipSettingsModalProps) => {
  const { allstarUser } = useUser();
  const { open: openClipQuality } = useClipQualitySettingsModal();
  const { currentConfig } = useClipQualityConfigs();
  const {
    optimisticValue,
    loading,
    isEligible,
    handleToggle,
    upsellOpen,
    UpsellPortal,
  } = useAutoSave();

  const avatar = allstarUser?.user?.avatar;
  const username = allstarUser?.user?.username;

  const handleOpenQuality = () => {
    onClose();
    setTimeout(() => openClipQuality(), 150);
  };

  const [clickedFilter, setClickedFilter] = useState<string | null>(null);

  const handleFilterClick = (filterId: string) => {
    if (filterId === "all") return;
    analytics.track("Auto-Save Filter - Clicked", {
      filter: filterId,
      source: "clip-settings",
    });
    setClickedFilter(filterId);
  };

  return (
    <>
      {createPortal(
        <Overlay onClick={onClose}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={onClose}>
              <HiX size={18} />
            </CloseButton>

            <HeaderRow>
              {avatar && <Avatar src={avatar} alt={username || ""} />}
              <HeaderText>
                <Title>Clipping Settings</Title>
                <Subtitle>
                  {username
                    ? `Manage clipping preferences for ${username}`
                    : "Manage your clip preferences"}
                </Subtitle>
              </HeaderText>
            </HeaderRow>

            <SettingsList>
              <SectionHeader>Auto-save</SectionHeader>

              <SettingRow>
                <SettingInfo>
                  <SettingLabel>
                    <HiShieldCheck size={16} />
                    Auto-save Clips
                    <AnimatedTooltip
                      content="When enabled, new clips are automatically saved so they won't expire after 72 hours"
                      position="right"
                      maxWidth={220}
                    >
                      <InfoIconWrap>
                        <FiInfo size={13} />
                      </InfoIconWrap>
                    </AnimatedTooltip>
                  </SettingLabel>
                  <SettingDescription>
                    New clips are saved automatically instead of expiring after
                    72 hours
                  </SettingDescription>
                </SettingInfo>
                <Checkbox
                  $checked={optimisticValue}
                  $loading={loading}
                  onClick={handleToggle}
                >
                  {optimisticValue && <IoCheckmarkSharp size={12} />}
                </Checkbox>
              </SettingRow>

              {optimisticValue && isEligible && (
                <FilterGroup>
                  {AUTO_SAVE_FILTERS.map((filter) => (
                    <FilterOption
                      key={filter.id}
                      $selected={filter.id === "all"}
                      onClick={() => handleFilterClick(filter.id)}
                    >
                      <RadioDot $selected={filter.id === "all"} />
                      <FilterInfo>
                        <FilterLabel>{filter.label}</FilterLabel>
                        {clickedFilter === filter.id ? (
                          <FilterFeedback>
                            We're still working on this, we'll let you know when
                            this is available!
                          </FilterFeedback>
                        ) : (
                          <FilterDescription>
                            {filter.description}
                          </FilterDescription>
                        )}
                      </FilterInfo>
                    </FilterOption>
                  ))}
                </FilterGroup>
              )}

              <Divider />

              <SectionHeader>Quality</SectionHeader>

              <SettingRow as="button" onClick={handleOpenQuality} $clickable>
                <SettingInfo>
                  <SettingLabel>
                    <ClipQualityIcon size={16} />
                    Clip Quality
                  </SettingLabel>
                  <SettingDescription>
                    {currentConfig
                      ? `Currently set to ${currentConfig}`
                      : "Configure resolution, framerate, and effects"}
                  </SettingDescription>
                </SettingInfo>
                <Chevron>&rsaquo;</Chevron>
              </SettingRow>
            </SettingsList>
          </Modal>
        </Overlay>,
        document.getElementById("root") || document.body,
      )}
      {upsellOpen && UpsellPortal}
    </>
  );
};

const Overlay = styled(Flex)`
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  bottom: 0;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 101;
`;

const Modal = styled(Flex)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  flex-direction: column;
  max-width: 440px;
  padding: 28px;
  position: relative;
  width: 90vw;
`;

const CloseButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 4px;
  position: absolute;
  right: 16px;
  top: 16px;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const HeaderRow = styled(Flex)`
  align-items: center;
  gap: 14px;
`;

const Avatar = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
  height: 40px;
  object-fit: cover;
  width: 40px;
`;

const HeaderText = styled(Flex)`
  flex-direction: column;
`;

const Title = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 20px;
  font-weight: 700;
`;

const Subtitle = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 14px;
  margin-top: 4px;
`;

const SettingsList = styled(Flex)`
  flex-direction: column;
  margin-top: 24px;
`;

const SectionHeader = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
  margin-top: 4px;
  text-transform: uppercase;
`;

const SettingRow = styled(Flex)<{ $clickable?: boolean }>`
  align-items: center;
  background: none;
  border: none;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  gap: 16px;
  justify-content: space-between;
  padding: 12px 0;
  text-align: left;
  transition: opacity 0.15s;
  width: 100%;

  ${({ $clickable }) =>
    $clickable &&
    `
    &:hover {
      opacity: 0.8;
    }
  `}
`;

const SettingInfo = styled(Flex)`
  flex-direction: column;
  gap: 2px;
`;

const SettingLabel = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 15px;
  font-weight: 600;
  gap: 6px;
`;

const SettingDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 13px;
`;

const InfoIconWrap = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: help;
  opacity: 0.6;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
`;

const Checkbox = styled.div<{ $checked: boolean; $loading?: boolean }>`
  align-items: center;
  background-color: ${({ $checked, theme }) =>
    $checked ? theme.colors.envy : "transparent"};
  border: 2px solid
    ${({ $checked, theme }) =>
      $checked ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 4px;
  color: white;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
  transition: all 150ms ease;
  width: 20px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const FilterGroup = styled(Flex)`
  flex-direction: column;
  gap: 2px;
  padding: 4px 0 8px 0;
`;

const FilterOption = styled(Flex)<{
  $selected?: boolean;
}>`
  align-items: flex-start;
  border-radius: 8px;
  cursor: pointer;
  gap: 10px;
  padding: 10px 12px;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha05};
  }
`;

const RadioDot = styled.div<{ $selected?: boolean }>`
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.envy : "transparent"};
  border: 2px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 50%;
  flex-shrink: 0;
  height: 16px;
  margin-top: 2px;
  transition: all 150ms ease;
  width: 16px;
`;

const FilterInfo = styled(Flex)`
  flex-direction: column;
  gap: 1px;
`;

const FilterLabel = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
`;

const FilterDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 12px;
`;

const FilterFeedback = styled(Text)`
  color: ${({ theme }) => theme.colors.envy};
  font-family: Barlow;
  font-size: 12px;
  font-weight: 500;
`;

const Chevron = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  flex-shrink: 0;
  font-size: 24px;
  line-height: 1;
`;

const Divider = styled(Box)`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  height: 1px;
  margin: 4px 0;
  width: 100%;
`;
